// Extensions Range — configure the auto-allocate range for agents.
import { useEffect, useState } from "react";
import { Loader2, Save, Hash } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useCurrentModulePerms } from "@/contexts/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";

export default function ExtensionsRangePage() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { canWrite } = useCurrentModulePerms();

  async function load() {
    setLoading(true);
    try { const r = await api.get("/admin/extensions/config"); setCfg(r.data); }
    catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);
  async function save() {
    if (cfg.range_end < cfg.range_start) { toast.error("End must be >= start"); return; }
    setSaving(true);
    try { await api.put("/admin/extensions/config", { range_start: Number(cfg.range_start), range_end: Number(cfg.range_end), prefix: cfg.prefix||"" }); toast.success("Saved"); load(); }
    catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  }

  if (loading || !cfg) return <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  const pct = Math.round((cfg.used / cfg.capacity) * 100);

  return (
    <div className="px-4 sm:px-6 py-6 max-w-3xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="ext-title">Extensions Range</h1>
          <p className="text-sm text-muted-foreground mt-1">When you grant an agent license, the next free extension in this range is auto-assigned.</p>
        </div>
        {canWrite && <Button data-testid="ext-save" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5"><Save className="w-4 h-4"/> Save</Button>}
      </div>
      <Card className="p-5 space-y-4">
        <div className="flex items-center gap-2"><Hash className="w-4 h-4 text-primary"/><h2 className="font-display font-bold">Range</h2></div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div><Label>Prefix (optional)</Label><Input data-testid="ext-prefix" value={cfg.prefix||""} onChange={(e)=>setCfg({...cfg, prefix:e.target.value})} placeholder="e.g., demo- or empty" /></div>
          <div><Label>Range start</Label><Input data-testid="ext-start" type="number" value={cfg.range_start} onChange={(e)=>setCfg({...cfg, range_start:Number(e.target.value)})} /></div>
          <div><Label>Range end</Label><Input data-testid="ext-end" type="number" value={cfg.range_end} onChange={(e)=>setCfg({...cfg, range_end:Number(e.target.value)})} /></div>
        </div>
        <div className="border-t pt-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span>Used <strong>{cfg.used}</strong> of <strong>{cfg.capacity}</strong> extensions</span>
            <Badge variant={pct > 80 ? "destructive" : "outline"}>{pct}%</Badge>
          </div>
          <Progress value={pct} />
          <div className="text-xs text-muted-foreground mt-2">Example next free: <code className="font-mono">{cfg.prefix||""}{cfg.range_start + cfg.used}</code></div>
        </div>
      </Card>
    </div>
  );
}
